import { Layout } from 'components/layouts';
import { flowRight, get, isFunction } from 'lodash/fp';
import PropTypes from 'prop-types';
import React, { createElement, Fragment, useEffect, useState } from 'react';
import { withProps, withState } from 'recompose';
import { formValues } from 'redux-form';
import { connect } from 'react-redux';

import { GET, POST } from '../../utils/httpMethods';
import dataCreationForm from '../dataCreationForm';
import dataProvider from '../dataProvider';
import { InputField, SelectField } from '../form/field';
import { VerticalLayout } from '../form/layout';
import FormButtons from '../FormButtons';
import { withSession } from '../sessions';
import formValidate from './formValidate';
import getUrl from './getUrl';
import types from './types';
import BeneficiaryForm from './types/scpi/Creation';

import './Creation.scss';
import withRouter from '../withRouter';
import RadioQuestion from '../InvestorProfile/RadioQuestion';
import FieldInfo from '../form/FieldInfo';

const WithProduct = formValues('product', 'simulationType')(({ product, simulationType, children }) => children(product, simulationType));

// Cache pour les données de session TopInvest
let topInvestSessionCache = null;

/**
 * Initialise une session TopInvest (avec cache)
 * Retourne le token, l'utilisateur et les portefeuilles
 */
const initTopInvestSession = async () => {
    // Retourner le cache si disponible
    if (topInvestSessionCache) {
        return topInvestSessionCache;
    }

    try {
        const response = await POST('/api/simulationsTopInvest/initSession');
        const data = await response.json();

        // Mettre en cache les données
        topInvestSessionCache = data;

        return data;
    } catch (error) {
        console.error('Erreur lors de l\'initialisation de la session TopInvest:', error);
        throw error;
    }
};

/**
 * Démarre une simulation TopInvest
 * Retourne l'URL de la simulation
 * @param {object} sessionData - Données de session (utilisateur, portefeuilles)
 * @param {string} ripId - ID du RIP
 * @param {string} beneficiary - Bénéficiaire (client, partner, clientAndPartner)
 * @param {string} name - Nom de la simulation
 * @param {string} product - Type de produit (scpiTopInvest, immobilierTopInvest)
 */
const startTopInvestSimulation = async (sessionData, ripId, beneficiary, name, product) => {
    try {
        const { user, portfolios } = sessionData;

        // Utiliser le premier portefeuille disponible
        const portfolioCode = portfolios && portfolios.length > 0
            ? portfolios[0].Code
            : null;

        if (!portfolioCode) {
            throw new Error('Aucun portefeuille disponible pour cet utilisateur');
        }

        const response = await POST('/api/simulationsTopInvest/departEnSimulation', {
            body: {
                ripId,
                beneficiary,
                codeEntiteGestion: user.codeEntiteGestion,
                portfolioCode,
                name,
                product,
            },
        });

        const data = await response.json();

        return data;
    } catch (error) {
        console.error('Erreur lors du démarrage de la simulation TopInvest:', error);
        throw error;
    }
};

const LoadingNextButton = () => (
    <div className="formNextAction">
        <button type="button"> Chargement
            <i className="fa fa-spinner fa-spin" aria-hidden="true" />
        </button>
    </div>
);

const CreationInner = ({
    handleSubmit,
    params: { id },
    rip,
    session,
    simulationType,
    isTopInvestLoading,
}) => {
    const topInvestProducts = {
        scpiTopInvest: { label: 'SCPI', formComponent: BeneficiaryForm },
    };
    const [hasValidOriasNumber, setHasValidOriasNumber] = useState(null);

    const check = session.permissions.mayCreateSimulation.bind(session.permissions, rip);
    const { instanceTheseis, instanceInovia, userHasTopInvestAccess } = session;

    const runCreationChecks = 'cohabitation' === rip.familySituation && rip.dualRip
        ? (product) => check(product) || check(product, 'partner')
        : check;

    useEffect(() => {
        (async () => {
            let validOriasNumberResponse = await GET(`/api/users/${session.id}/validOriasNumber`);
            validOriasNumberResponse = await validOriasNumberResponse.json();
            setHasValidOriasNumber(validOriasNumberResponse);
        })();
    }, []);

    // Pré-charger la session TopInvest quand l'utilisateur sélectionne ce type
    useEffect(() => {
        if ('isTopInvestSimulation' === simulationType && session.userHasTopInvestAccess) {
            initTopInvestSession().catch(() => {});
        }
    }, [simulationType]);

    if (null !== hasValidOriasNumber) {
        if (!hasValidOriasNumber || !instanceTheseis) {
            delete types.per;
        }
    }

    return (
        <Layout className="simulation-form creation-form" hasFooter>
            <div>
                <form onSubmit={handleSubmit} id="main-form">
                    <div>
                        <div className="simulation-form-inner">
                            <div className="container">
                                <VerticalLayout>
                                    <InputField name="name" title="Nom de la simulation" type="text" required />
                                    <RadioQuestion
                                        choices={{
                                            isEripSimulation: 'Simulation eRIP',
                                            isTopInvestSimulation: 'Simulation TopInvest',
                                        }}
                                        name="simulationType"
                                        title="Type"
                                        required
                                        disabled={!userHasTopInvestAccess}
                                        fieldInfo={(key) => ('isTopInvestSimulation' === key && (instanceInovia && !userHasTopInvestAccess) ? (
                                            <FieldInfo id="topInvestAccessInfo" placement="left">
                                                <div>
                                                    <p>
                                                        Veuillez vous rapprocher du service commercial si vous êtes intéressés par le simulateur TopInvest.
                                                    </p>
                                                </div>
                                            </FieldInfo>
                                        ) : null)}
                                    />
                                    <WithProduct>
                                        {(product, typeSimulation) => {
                                            const productList = 'isEripSimulation' === typeSimulation ? types : topInvestProducts;
                                            const AdditionalFieldComponent = get([product, 'formComponent'], productList);

                                            return (
                                                <Fragment>
                                                    <SelectField name="product" title="Produit" required>
                                                        <option />
                                                        {Object
                                                            .entries(productList)
                                                            .filter(([productKey]) => productKey !== 'pinel')
                                                            .map(([productKey, { label }]) => (
                                                                runCreationChecks(productKey)
                                                                    ? <option value={productKey} key={productKey}>{label}</option>
                                                                    : null))}
                                                    </SelectField>
                                                    {AdditionalFieldComponent
                                                        ? createElement(AdditionalFieldComponent, { rip })
                                                        : null}
                                                </Fragment>
                                            );
                                        }}
                                    </WithProduct>
                                </VerticalLayout>
                            </div>
                        </div>
                        <FormButtons
                            back={`/rip/${id}/simulations/`}
                            list={`/rip/${id}/simulations/`}
                            isHiddenButton={!navigator.onLine}

                            {...(isTopInvestLoading ? { nextButtonComponent: LoadingNextButton } : {})}
                        />
                    </div>
                </form>
            </div>

        </Layout>
    );
};

CreationInner.propTypes = {
    handleSubmit: PropTypes.func.isRequired,
    params: PropTypes.shape({
        id: PropTypes.string.isRequired,
    }).isRequired,
    rip: PropTypes.shape(),
    simulationType: PropTypes.string,
    isTopInvestLoading: PropTypes.bool,
    session: PropTypes.shape({
        instanceTheseis: PropTypes.bool,
        instanceInovia: PropTypes.bool,
        id: PropTypes.string,
        userHasTopInvestAccess: PropTypes.bool,
        entityHasTopInvestAccess: PropTypes.bool,
        permissions: PropTypes.shape({
            mayCreateSimulation: PropTypes.func,
        }),
    }).isRequired,
};

const Creation = flowRight([
    withRouter,
    withProps(({ params: { id } }) => ({
        initialValues: {
            ripId: id,
            beneficiary: 'client',
            simulationType: 'isEripSimulation',
        },
    })),
    dataProvider({
        id: ({ params: { id } }) => id,
        type: 'rip',
        wait: true,
    }),
    withSession,
    withState('isTopInvestLoading', 'setIsTopInvestLoading', false),
    connect((state) => ({
        simulationType: get('form.simulation-creation.values.simulationType', state),
    })),
    dataCreationForm({
        form: 'simulation-creation',
        onSubmitSuccess: async (result, dispatch, props) => {
            const { navigate, values, rip } = props;
            const { simulationType } = values;

            if ('isEripSimulation' === simulationType) {
                navigate(getUrl(result));

                return;
            }

            // Simulation TopInvest
            try {
                props.setIsTopInvestLoading(true);

                // Initialiser la session
                const sessionData = await initTopInvestSession();

                // Récupérer les données de la simulation
                const { beneficiary = 'client', name, product } = values;

                // Démarrer la simulation avec nom et produit
                const simulationResult = await startTopInvestSimulation(
                    sessionData,
                    rip.id,
                    beneficiary,
                    name,
                    product,
                );

                // Ouvrir l'URL de simulation dans une nouvelle fenêtre
                if (simulationResult.simulationUrl) {
                    window.open(simulationResult.simulationUrl, '_blank');
                }

                props.setIsTopInvestLoading(false);
                // Naviguer vers la liste des simulations
                navigate(`/rip/${rip.id}/simulations/`);
            } catch (error) {
                props.setIsTopInvestLoading(false);
                console.error('Erreur lors de la création de la simulation TopInvest:', error);
            }
        },
        type: 'simulation',
        // eslint-disable-next-line consistent-return
        onSubmitData: (values, props) => {
            const productValues = get([values.product, 'initialValues'], types);

            return ({
                ...(isFunction(productValues) ? productValues(values, props) : productValues),
                ...values,
            });
        },
        validate: formValidate,
    }),
])(CreationInner);

export default Creation;
